import { useReducer } from 'react'
import { branch, renderComponent, renderNothing } from 'react-recompose'
import FormGroup from '@mui/material/FormGroup'
import FormControl from '@mui/material/FormControl'
import { getPath, identity, getRoundedRange } from '@anewgo/functions'
import clsx from 'clsx'
import Loading from './Loading/Loading'
import Slider from './Slider/Slider'
import * as reducers from '../store/reducers'
import { isInventoryLot } from '@anewgo/utils/dist/siteplans'

const SHALLOW_MERGE = 'SHALLOW_MERGE'

export const plansStateReducer = (state, action) => {
  switch (action.type) {
    case SHALLOW_MERGE:
      return {
        ...state,
        ...action.payload,
      }
    default:
      return state
  }
}

export const AttributeSliders = (props) => {
  const { classes, sliderState, pricing, wide, dispatch } = props
  const {
    sqftDomain,
    sqftValues,
    brDomain,
    brValues,
    baDomain,
    baValues,
    priceDomain,
    priceValues,
  } = sliderState
  const baseStyles = { gridTemplateColumns: wide ? '1fr 1fr' : '1fr' }
  // use the dispatch func that is passed in as that will correctly update
  // the store's context
  useReducer(plansStateReducer, {
    sqftDomain,
    sqftValues,
    brDomain,
    brValues,
    baDomain,
    baValues,
    priceDomain,
    priceValues,
  })
  const columnOne = () => (
    <FormGroup row>
      {pricing && priceValues[0] !== priceValues[1] && priceValues[0] !== 0 && (
        <FormControl
          className={clsx(classes.formControl, classes.sliderFormControl)}
        >
          <Slider
            mode={2}
            sliderLabel={'PRICE'}
            domain={priceDomain}
            step={1000}
            initialValues={getRoundedRange(1000)(priceValues)}
            format={(d) => `${d / 1000}K`}
            formatHandle={(d) => `$${d / 1000}K`}
            onChange={(values) => {
              dispatch(reducers.setPriceRange(values))
            }}
            tickCount={3}
          />
        </FormControl>
      )}
      {sqftValues[0] !== sqftValues[1] && (
        <FormControl
          className={clsx(classes.formControl, classes.sliderFormControl)}
        >
          <Slider
            mode={2}
            sliderLabel={'SQ FT'}
            domain={sqftDomain}
            initialValues={sqftValues}
            step={1}
            onChange={(values) => {
              dispatch(reducers.setSqftRange(values))
            }}
            tickCount={3}
          />
        </FormControl>
      )}
      {!wide && (
        <FormGroup row>
          {brValues[0] !== brValues[1] && (
            <FormControl
              className={clsx(classes.formControl, classes.sliderFormControl)}
            >
              <Slider
                mode={1}
                sliderLabel={'BR'}
                step={1}
                domain={brDomain}
                initialValues={brValues}
                onChange={(values) => {
                  dispatch(reducers.setBedroomRange(values))
                }}
                tickCount={3}
              />
            </FormControl>
          )}
          {baValues[0] !== baValues[1] && (
            <FormControl
              className={clsx(classes.formControl, classes.sliderFormControl)}
            >
              <Slider
                mode={1}
                sliderLabel={'BA'}
                step={0.5}
                domain={baDomain}
                initialValues={baValues}
                onChange={(values) => {
                  dispatch(reducers.setBathroomRange(values))
                }}
                tickCount={3}
              />
            </FormControl>
          )}
        </FormGroup>
      )}
    </FormGroup>
  )

  const columnTwo = () => {
    if (!wide) {
      return null
    }
    return (
      <FormGroup row>
        {brValues[0] !== brValues[1] && (
          <FormControl
            className={clsx(classes.formControl, classes.sliderFormControl)}
          >
            <Slider
              mode={1}
              sliderLabel={'BR'}
              step={1}
              domain={brDomain}
              initialValues={brValues}
              onChange={(values) => {
                dispatch(reducers.setBedroomRange(values))
              }}
              tickCount={3}
            />
          </FormControl>
        )}
        {baValues[0] !== baValues[1] && (
          <FormControl
            className={clsx(classes.formControl, classes.sliderFormControl)}
          >
            <Slider
              mode={1}
              sliderLabel={'BA'}
              step={0.5}
              domain={baDomain}
              initialValues={baValues}
              onChange={(values) => {
                dispatch(reducers.setBathroomRange(values))
              }}
              tickCount={3}
            />
          </FormControl>
        )}
      </FormGroup>
    )
  }
  return (
    <div className={classes.slidersContainer} style={baseStyles}>
      {columnOne()}
      {columnTwo()}
    </div>
  )
}

export const getInventories = (lots) => {
  let result = []
  lots.forEach((lot) => {
    if (isInventoryLot(lot)) {
      result = result.concat(lot.inventory)
    }
  })
  return result
}

export const computeSqftDomain = (sqftMin, sqftMax) => {
  return [Math.floor(sqftMin / 100) * 100, Math.ceil(sqftMax / 100) * 100]
}

export const computeBaDomain = (baMin, baMax) => {
  return [Math.floor(baMin) - 0.5, Math.ceil(baMax) + 0.5]
}

export const computeBrDomain = (brMin, brMax) => {
  return [Math.floor(brMin) - 1, Math.ceil(brMax) + 1]
}

export const computePriceDomain = (costMin, costMax) => {
  return [Math.floor(costMin / 1000) * 1000, Math.ceil(costMax / 1000) * 1000]
}

export const requireProps = ({ keys, loadingSpinner }) => {
  const propsAreInvalid = (props) =>
    keys.reduce((acc, key) => acc || !props[key], false)
  return branch(
    propsAreInvalid,
    loadingSpinner ? renderComponent(() => <Loading />) : renderNothing
  )
}

export const computeMinMax = (plans, inventories) => {
  let baMin = Number.MAX_SAFE_INTEGER
  let baMax = 0
  let sqftMin = Number.MAX_SAFE_INTEGER
  let sqftMax = 0
  let brMin = Number.MAX_SAFE_INTEGER
  let brMax = 0
  let costMin = Number.MAX_SAFE_INTEGER
  let costMax = 0
  plans.forEach((plan) => {
    sqftMin = Math.min(plan.sizeRange.min, sqftMin)
    sqftMax = Math.max(plan.sizeRange.max, sqftMax)
    baMin = Math.min(plan.bathRange.min, baMin)
    baMax = Math.max(plan.bathRange.max, baMax)
    brMin = Math.min(plan.bedRange.min, brMin)
    brMax = Math.max(plan.bedRange.max, brMax)
    costMin = Math.min(plan.costRange.min, costMin)
    costMax = Math.max(plan.costRange.max, costMax)
  })
  inventories.forEach((inventory) => {
    if (inventory.sqft && inventory.sqft !== 0) {
      sqftMin = Math.min(sqftMin, inventory.sqft)
      sqftMax = Math.max(sqftMax, inventory.sqft)
    }
    if (inventory.baths && inventory.baths !== 0) {
      baMin = Math.min(inventory.baths, baMin)
      baMax = Math.max(inventory.baths, baMax)
    }
    if (inventory.beds && inventory.beds !== 0) {
      brMin = Math.min(inventory.beds, brMin)
      brMax = Math.max(inventory.beds, brMax)
    }
    if (inventory.price && inventory.price !== 0) {
      costMin = Math.min(inventory.price, costMin)
      costMax = Math.max(inventory.price, costMax)
    }
  })
  if (baMin === Number.MAX_SAFE_INTEGER) {
    baMin = 0
  }
  if (brMin === Number.MAX_SAFE_INTEGER) {
    brMin = 0
  }
  if (sqftMin === Number.MAX_SAFE_INTEGER) {
    sqftMin = 0
  }
  if (costMin === Number.MAX_SAFE_INTEGER) {
    costMin = 0
  }
  return { baMin, baMax, brMin, brMax, sqftMin, sqftMax, costMin, costMax }
}

export const spinnerIf = (propsAreInvalid) => {
  return branch(
    propsAreInvalid,
    renderComponent(() => <Loading />)
  )
}

const updateRangeValues = (srcObj, attr, range) => {
  const rangeMin = range[0] || Number.MAX_SAFE_INTEGER
  const rangeMax = range[1] || 0
  const min = srcObj[attr].min
  const max = srcObj[attr].max

  // Update max/min range if the attribute value is defined and greater than/less than
  if (min && min < rangeMin) {
    range[0] = min
  }
  if (max && max > rangeMax) {
    range[1] = max
  }
}

export const getPlanSpecs = (plans = null, elevations = null) => {
  const bedRange = [null, null]
  const bathRange = [null, null]
  const sqftRange = [null, null]
  const costRange = [null, null]

  const updateAllRangeValues = (tgt) => {
    updateRangeValues(tgt, 'bedRange', bedRange)
    updateRangeValues(tgt, 'bathRange', bathRange)
    updateRangeValues(tgt, 'sizeRange', sqftRange)
    updateRangeValues(tgt, 'costRange', costRange)
  }

  if (getPath('length')(plans) > 0) {
    plans.forEach(updateAllRangeValues)
  } else if (getPath('length')(elevations) > 0) {
    elevations.forEach(updateAllRangeValues)
  }

  const getSpecs = (range, { label, transform = identity }) => {
    const [min, max] = range
    const decorate = (text) => (label ? `${text} ${label}` : text)

    if (!min) {
      return null
    } else if (min === max) {
      return decorate(transform(min))
    } else {
      return decorate(`${transform(min)} - ${transform(max)}`)
    }
  }

  return {
    bed: getSpecs(bedRange, { label: 'BR' }),
    bath: getSpecs(bathRange, { label: 'BA' }),
    size: getSpecs(sqftRange, { label: 'SQ FT' }),
    cost: getSpecs(costRange, {
      transform: (cost) =>
        cost
          ?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
          .split('.')[0],
    }),
  }
}

export const getBaseElevations = (baseElevs) => {
  if (!baseElevs) {
    return null
  }
  if (baseElevs.length === 1) {
    return baseElevs
  }
  const elevations = baseElevs.filter((tgt) => {
    const { mirrorElevationId, mirrorRolePrimary } = tgt
    if (!mirrorElevationId) {
      return true
    } else if (mirrorRolePrimary) {
      return true
    }
    return false
  })
  return elevations
}

const preventScrolling = (evt) => {
  evt.preventDefault()
}

export const addPreventScrollListener = () => {
  document.addEventListener('touchmove', preventScrolling, { passive: false })
}

export const removePreventScrollListener = () => {
  document.removeEventListener('touchmove', preventScrolling)
}

export const getEventCoordinates = (e) => {
  if (e.clientX || e.clientY) {
    return [e.clientX, e.clientY]
  } else if (e.touches && e.touches.length) {
    return [e.touches[0].clientX, e.touches[0].clientY]
  } else if (e.changedTouches && e.changedTouches.length) {
    return [e.changedTouches[0].clientX, e.changedTouches[0].clientY]
  }

  return []
}

export const calculateMortPayment = (cost, rate, term, downPaymentAmt) => {
  // M = P [ i(1 + i)^n ] / [ (1 + i)^n – 1]
  const interest = rate / 100 / 12
  const totalPayments = term * 12
  const totalAmount = cost - (downPaymentAmt || 0)
  const payment =
    (totalAmount * interest * Math.pow(1 + interest, totalPayments)) /
    (Math.pow(1 + interest, totalPayments) - 1)
  return payment.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}
